// File: /src/services/marketDataService.js

import axios from 'axios';

const SOURCES = {
	dollar: 'https://economia.awesomeapi.com.br/json/last/USD-BRL',
	euro: 'https://economia.awesomeapi.com.br/json/last/EUR-BRL',
	pound: 'https://economia.awesomeapi.com.br/json/last/GBP-BRL',
	bitcoin: 'https://economia.awesomeapi.com.br/json/last/BTC-BRL',
	ethereum: 'https://economia.awesomeapi.com.br/json/last/ETH-BRL',
};

const SOURCE_KEYS = {
	dollar: 'USDBRL',
	euro: 'EURBRL',
	pound: 'GBPBRL',
	bitcoin: 'BTCBRL',
	ethereum: 'ETHBRL',
};

const DEFAULT_VALUE = 0;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withRetry = async (operation, { retries = 2, delay = 400 } = {}) => {
	let attempt = 0;
	let lastError;
	while (attempt <= retries) {
		try {
			return await operation();
		} catch (error) {
			lastError = error;
			if (attempt === retries) {
				throw lastError;
			}
			await sleep(delay * (attempt + 1));
			attempt += 1;
		}
	}
	throw lastError;
};

const fetchWithTimeout = async (url, timeout = 3000) => {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), timeout);

	try {
		const response = await axios.get(url, {
			signal: controller.signal,
		});
		return response.data;
	} finally {
		clearTimeout(timer);
	}
};

export const fetchMarketSnapshot = async () => {
	const entries = Object.entries(SOURCES);
	const results = await Promise.allSettled(
		entries.map(([key, url]) =>
			withRetry(() => fetchWithTimeout(url), {
				retries: 2,
				delay: 500,
			}).then((data) => ({ key, data }))
		),
	);

	return results.reduce((acc, result, index) => {
		if (result.status === 'fulfilled') {
			const { key, data } = result.value;
			const sourceKey = SOURCE_KEYS[key];
			const value = parseFloat(data?.[sourceKey]?.bid ?? DEFAULT_VALUE);
			acc[key] = Number.isFinite(value) ? value : DEFAULT_VALUE;
		} else {
			const key = entries[index][0];
			acc[key] = DEFAULT_VALUE;
		}
		return acc;
	}, {});
};

export default fetchMarketSnapshot;
